/**
 * The Foundry import's DTOs (CS r6a deliverable 184; DECISIONS 213, 216, 220 and 222): one
 * `actor_import` row per actor of an uploaded file (`ActorImportV1`), its detail with the mapping
 * report, the refresh diff and the biography (`ActorImportDetailV1`), the upload's answer
 * (`FoundryImportFileV1`) and the apply (`ActorImportApplyV1`, `ActorImportAppliedV1`). The routes
 * are `routes/foundry.ts` (owner 2.I); the values are `rows-enums.ts`'s Foundry section.
 *
 * An import names Foundry ids and paths, never a value of the GM's file: a warning is a code and a
 * positional path (`items[7].system.activities[0]`), a module flag a module id and a count.
 */
import { ACTOR_IMPORT_STATUSES, ACTOR_IMPORT_TARGETS, ACTOR_IMPORT_WARNING_CODES, FOUNDRY_ACTOR_TYPES, FOUNDRY_DETECTED_LAYOUTS, FOUNDRY_IMPORT_STATE_REASONS, FOUNDRY_INVALID_BODY_ISSUES, FOUNDRY_REFUSAL_CODES, IDENTITY_OUTCOMES, IDENTITY_REASONS, IDENTITY_SOURCES, REFRESH_CHOICES, REFRESH_OPS, } from '../domain/rows-enums.js';
import { z } from '../zod.js';
import { IsoTimestamp, JsonValue, RowVersion, Uuid } from './common.js';
import { DocTitle, TypeKey } from './documents.js';
import { Cursor } from './list.js';
import { queryInt } from './query.js';
export { ACTOR_IMPORT_STATUSES, ACTOR_IMPORT_TARGETS, ACTOR_IMPORT_WARNING_CODES, FOUNDRY_ACTOR_TYPES, FOUNDRY_DETECTED_LAYOUTS, FOUNDRY_IMPORT_STATE_REASONS, FOUNDRY_INVALID_BODY_ISSUES, FOUNDRY_REFUSAL_CODES, IDENTITY_OUTCOMES, IDENTITY_REASONS, IDENTITY_SOURCES, REFRESH_CHOICES, REFRESH_OPS, };
/** A `foundry_import_state` reason and a 422 issue of the import's routes (CS r6b, 805.3). */
export const FoundryImportStateReason = z.enum(FOUNDRY_IMPORT_STATE_REASONS);
export const FoundryInvalidBodyIssue = z.enum(FOUNDRY_INVALID_BODY_ISSUES);
/** The largest Foundry file accepted: 25 MiB (DECISIONS 222 ruling 7). */
export const FOUNDRY_IMPORT_MAX_BYTES = 25 * 1024 * 1024;
/** The media types a browser sends a `.json` export under. */
export const FOUNDRY_IMPORT_MIME_TYPES = [
    'application/json',
    'application/octet-stream',
    'text/plain',
];
/** The macro the GM runs in Foundry, served by `foundry.macro.get`. */
export const FOUNDRY_MACRO_FILE = 'packages/foundry-macro/release/codex-export.js';
export const ActorImportStatus = z.enum(ACTOR_IMPORT_STATUSES);
export const IdentitySource = z.enum(IDENTITY_SOURCES);
export const IdentityOutcome = z.enum(IDENTITY_OUTCOMES);
export const IdentityReason = z.enum(IDENTITY_REASONS);
export const FoundryDetectedLayout = z.enum(FOUNDRY_DETECTED_LAYOUTS);
export const FoundryActorType = z.enum(FOUNDRY_ACTOR_TYPES);
export const FoundryRefusalCode = z.enum(FOUNDRY_REFUSAL_CODES);
export const RefreshOp = z.enum(REFRESH_OPS);
export const RefreshChoice = z.enum(REFRESH_CHOICES);
export const ActorImportTarget = z.enum(ACTOR_IMPORT_TARGETS);
export const ActorImportWarningCode = z.enum(ACTOR_IMPORT_WARNING_CODES);
/**
 * A warning's code: a bounded slug (open point 50), so 2.G's `MAPPING_WARNING_CODES` and
 * `ACTOR_IMPORT_WARNING_CODES` both fit without contracts importing the mapper; 2.I s1 asserts
 * that every code it stores is in one of the two.
 */
export const ImportWarningSlug = z.string().regex(/^[a-z0-9_]{1,64}$/);
/** A path into the actor export: generalized or positional, never a value. */
const ActorPath = z.string().min(1).max(500);
const FileName = z.string().min(1).max(255);
const VersionText = z.string().min(1).max(64);
const Count = z.number().int().min(0);
/** The type a preview proposes for the new document (GM decision 7's default for an NPC). */
export const PROPOSED_TYPE_KEYS = ['pc', 'npc', 'creature'];
/** One proposal of the identity chain: a sheet, its document and the step that named it. */
export const IdentityProposalV1 = z.strictObject({
    sheetId: Uuid,
    documentId: Uuid,
    title: DocTitle,
    source: IdentitySource,
    /** The fingerprint step's item-id overlap, 0 to 1. */
    overlap: z.number().min(0).max(1).optional(),
});
/** What the identity chain found (2.G deliverable 16). */
export const ActorImportIdentityV1 = z.strictObject({
    outcome: IdentityOutcome,
    /** A match the GM is not asked about: steps 1 to 3 agree and the world matches. */
    silent: z.boolean(),
    /** The step of a match; null when proposed or new. */
    source: IdentitySource.nullable(),
    proposals: z.array(IdentityProposalV1).max(20),
    reasons: z.array(IdentityReason).max(IDENTITY_REASONS.length),
});
/**
 * The compendium entry an NPC came from (`exportSource` or `compendiumSource`): the SRD reference
 * document it matches (null until 2.I s2b) and whether the actor is unchanged from it, which
 * alone allows a `link`.
 */
export const ActorImportCompendiumV1 = z.strictObject({
    uuid: z.string().min(1).max(500),
    referenceDocumentId: Uuid.nullable(),
    unchanged: z.boolean(),
});
/** A warning of the mapping or of the import: a code and a path. */
export const ActorImportWarningV1 = z.strictObject({ code: ImportWarningSlug, path: ActorPath });
/** The module flags of the actor and its items: counted, never mapped (DECISIONS 213 ruling 3). */
export const ActorImportModuleFlagsV1 = z.strictObject({
    /** Module ids: public software names. */
    modules: z.array(z.string().min(1).max(100)).max(50),
    leafCount: Count,
});
/**
 * One `actor_import` row: one actor of an uploaded file (`entry`, its place in the file; 0 for a
 * whole-file refusal). A refused row has its `refusal` and nulls where nothing was read.
 * `sheetId` and `documentId` are null until applied, and stay null after a `link` (which writes
 * no document or sheet); `identitySource` is the step that proposed the target the GM chose, null
 * for a target he chose unproposed or `new`.
 */
export const ActorImportV1 = z.strictObject({
    id: Uuid,
    rv: RowVersion,
    assetId: Uuid,
    fileName: FileName,
    entry: z.number().int().min(0).max(99),
    status: ActorImportStatus,
    refusal: FoundryRefusalCode.nullable(),
    actorType: FoundryActorType.nullable(),
    name: z.string().min(1).max(500).nullable(),
    systemVersion: VersionText.nullable(),
    coreVersion: VersionText.nullable(),
    layout: FoundryDetectedLayout.nullable(),
    proposedTypeKey: z.enum(PROPOSED_TYPE_KEYS).nullable(),
    identity: ActorImportIdentityV1.nullable(),
    compendium: ActorImportCompendiumV1.nullable(),
    warnings: z.array(ActorImportWarningV1).max(200),
    moduleFlags: ActorImportModuleFlagsV1,
    /** Whether the macro's derived snapshot came with the actor (DECISIONS 213 ruling 4). */
    snapshotPresent: z.boolean(),
    /** Whether Foundry holds a biography, which apply never writes (ruling 17). */
    hasBiography: z.boolean(),
    sheetId: Uuid.nullable(),
    documentId: Uuid.nullable(),
    identitySource: IdentitySource.nullable(),
    /** The change batch of the apply (`change_batch.id`); null until applied. */
    batchId: Uuid.nullable(),
    appliedAt: IsoTimestamp.nullable(),
    undoneAt: IsoTimestamp.nullable(),
    createdAt: IsoTimestamp,
    updatedAt: IsoTimestamp,
});
/** The mapper's report (2.G's `MappingReport`), as the detail shows it: paths and counts only. */
export const ActorImportMappingReportV1 = z.strictObject({
    /** The mapper's registry key, such as `dnd5e@5.0-5.2`. */
    mapper: z.string().min(1).max(64),
    mapperVersion: z.number().int().min(1),
    /** Generalized leaf paths no coverage row accounts for; sorted. */
    unmappedPaths: z.array(ActorPath).max(2000),
    counts: z.strictObject({
        items: Count,
        activities: Count,
        effects: Count,
        transferredEffects: Count,
    }),
});
/** One operation of the refresh diff (2.G's `planRefresh`): one origin path at most once. */
export const RefreshDiffOpV1 = z.strictObject({
    path: ActorPath,
    op: RefreshOp,
    /** The values at the path: the sheet's now, the file's, and the last applied import's. */
    current: JsonValue.nullable(),
    incoming: JsonValue.nullable(),
    base: JsonValue.nullable(),
    choices: z.array(RefreshChoice).max(REFRESH_CHOICES.length),
    default: RefreshChoice,
});
/** The refresh diff against a matched sheet; `nameChanged` proposes a rename, never applies one. */
export const RefreshDiffV1 = z.strictObject({
    ops: z.array(RefreshDiffOpV1).max(2000),
    nameChanged: z.boolean(),
});
/** The largest biography the detail carries, in code points (sanitized HTML). */
export const FOUNDRY_BIOGRAPHY_MAX = 200_000;
/**
 * `foundry.imports.get`: the row with its mapping report (null for a file that never reached a
 * mapper), the refresh diff (null without a matched or chosen sheet) and Foundry's biography,
 * sanitized, for 2.I-2's "Add Foundry's biography to the page" (never written by apply; DECISIONS
 * 213 ruling 17); null while 2.G's sanitizer is not exported (DECISIONS 222 ruling 9).
 */
export const ActorImportDetailV1 = ActorImportV1.extend({
    mappingReport: ActorImportMappingReportV1.nullable(),
    diff: RefreshDiffV1.nullable(),
    /**
     * At most 200,000 code points: under this package's `z`, `.max` counts code points (an astral
     * character is one), the convention `src/zod.ts` states, so the plain bound needs no refinement.
     */
    biography: z.string().max(FOUNDRY_BIOGRAPHY_MAX).nullable(),
});
/** What a file was read as: the Codex macro's party file, one Export Data actor, or neither. */
export const FOUNDRY_FILE_FORMATS = ['macro', 'export', 'unknown'];
/** `foundry.imports.create`: the stored file and its rows, one per actor (at most 100). */
export const FoundryImportFileV1 = z.strictObject({
    assetId: Uuid,
    fileName: FileName,
    format: z.enum(FOUNDRY_FILE_FORMATS),
    imports: z.array(ActorImportV1).max(100),
});
/** The form fields of `foundry.imports.create` beside its `file`: none. */
export const FoundryImportFieldsV1 = z.strictObject({});
/** `/api/foundry/imports/:importId`. */
export const ActorImportParams = z.strictObject({ importId: Uuid });
/** `foundry.imports.list`: newest first, by status or by the document an import applied to. */
export const ActorImportListQueryV1 = z.strictObject({
    status: ActorImportStatus.optional(),
    documentId: Uuid.optional(),
    cursor: Cursor.optional(),
    limit: queryInt(1, 200).default(50),
});
/**
 * `foundry.imports.get`: `targetSheetId` names the sheet the refresh diff is computed against;
 * by default the silent match, else the first proposal.
 */
export const ActorImportGetQueryV1 = z.strictObject({ targetSheetId: Uuid.optional() });
/** `foundry.imports.reject`. */
export const ActorImportRejectV1 = z.strictObject({ baseRv: RowVersion });
/**
 * Where an apply writes: a new document of the chosen type (`title` defaulting to the actor's
 * name); a canon document whose type has `has_sheet` (its primary sheet if it has none, else a
 * new one under `label`); or a refresh of a sheet, guarded by its `baseVersion`.
 */
export const ActorImportTargetV1 = z.discriminatedUnion('kind', [
    z.strictObject({ kind: z.literal('new'), typeKey: TypeKey, title: DocTitle.optional() }),
    z.strictObject({
        kind: z.literal('document'),
        documentId: Uuid,
        label: z.string().min(1).max(200).optional(),
    }),
    z.strictObject({ kind: z.literal('sheet'), sheetId: Uuid, baseVersion: RowVersion }),
]);
/** An NPC that came from a compendium: a link to the SRD entry, or a variant of it. */
export const SRD_ORIGIN_CHOICES = ['link', 'variant'];
/** The choices of an apply, stored as applied (`actor_import.choices`). */
export const ActorImportChoicesV1 = z
    .strictObject({
    target: ActorImportTargetV1.optional(),
    srdOrigin: z.enum(SRD_ORIGIN_CHOICES).optional(),
    refresh: z
        .array(z.strictObject({ path: ActorPath, choice: RefreshChoice }))
        .max(2000)
        .optional(),
})
    .refine((c) => (c.target === undefined) === (c.srdOrigin === 'link'), {
    message: 'A link names no target; every other apply names one',
    path: ['target'],
});
/** `foundry.imports.apply`: a stale `baseRv` answers 409 `row_conflict`. */
export const ActorImportApplyV1 = z.strictObject({
    baseRv: RowVersion,
    choices: ActorImportChoicesV1,
});
/**
 * What an apply wrote: the import (now `applied`), its document and sheet, or for a `link` the
 * reference document it names (no document, sheet or `foundry_map` row is written).
 */
export const ActorImportAppliedV1 = z.strictObject({
    import: ActorImportV1,
    documentId: Uuid.nullable(),
    sheetId: Uuid.nullable(),
    linkedReferenceId: Uuid.nullable(),
});
//# sourceMappingURL=foundry.js.map